import {
  useInfiniteQuery,
  QueryKey,
  UseInfiniteQueryOptions,
} from '@tanstack/react-query'
import { Paginated } from '../types/IPaginationResponse'

type InfiniteResult<T> = {
  items: T[]
  /** Undefined until the first page lands, or if the endpoint answers without an envelope. */
  meta: Paginated<T>['meta'] | undefined
}

interface InfiniteOptions<TParams, TItem>
  extends Omit<
    UseInfiniteQueryOptions<
      Paginated<TItem>,
      Error,
      InfiniteResult<TItem>
    >,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  > {
  queryKey: QueryKey
  fetchFn: (
    params: TParams & { page: number }
  ) => Promise<Paginated<TItem>>
  params: TParams
  initialPage?: number
}

export function useInfinitePaginated<
  TParams extends object,
  TItem
>({
  queryKey,
  fetchFn,
  params,
  initialPage = 1,
  ...options
}: InfiniteOptions<TParams, TItem>) {
  return useInfiniteQuery<
    Paginated<TItem>,
    Error,
    InfiniteResult<TItem>
  >({
    queryKey,

    initialPageParam: initialPage,

    queryFn: ({ pageParam }) =>
      fetchFn({
        ...params,
        page: pageParam as number,
      }),

    // Runs inside the query observer's notification, NOT inside the fetch promise. A throw here
    // escapes notifyManager uncaught, the subscriber never receives the success result, and the
    // hook stays `pending` forever while the network tab shows a healthy 200. So never assume the
    // envelope: an endpoint that answers with the wrong shape degrades to "no more pages".
    getNextPageParam: (lastPage) => {
      const meta = lastPage?.meta
      if (!meta) return undefined
      const { page, limit, total } = meta
      return page * limit < total
        ? page + 1
        : undefined
    },

    select: (data) => ({
      items: data.pages.flatMap((p) => p?.items ?? []),
      meta: data.pages.at(-1)?.meta,
    }),

    ...options, // 🔥 критично важливо
  })
}
